import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { siteApi } from "@/lib/api";

export const NOTIFICATION_REFRESH_MS = 10000;
const NOTIFICATION_SYNC_EVENTS = ["notificationSync", "noteRead", "inquiryRead"];

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const getNoteUnreadCount = (payload = {}) =>
  toNumber(payload.admin_note_count) +
  toNumber(payload.note_count) +
  toNumber(payload.unread_note_count);

const getInquiryUnreadCount = (payload = {}) =>
  toNumber(payload.admin_inquiry_answer_count);

const getUnreadCount = (payload = {}) =>
  getNoteUnreadCount(payload) + getInquiryUnreadCount(payload);

const useMemberNotifications = (enabled, options = {}) => {
  const suppressedTypes = options.suppressedTypes || {};
  const [counts, setCounts] = useState({});
  const [alerts, setAlerts] = useState([]);
  const isMountedRef = useRef(false);
  const requestInFlightRef = useRef(false);
  const previousCountsRef = useRef({ note: 0, inquiry: 0 });

  const pushAlert = useCallback((type, count) => {
    const alertConfig =
      type === "inquiry"
        ? {
            title: "고객센터 답변",
            message: "고객문의 답변이 도착했습니다.",
            to: "/customer-service-center",
          }
        : {
            title: "쪽지 알림",
            message: "새 쪽지가 도착했습니다.",
            to: "/notice-board",
          };

    setAlerts((previous) => {
      if (previous.some((item) => item.type === type)) {
        return previous.map((item) =>
          item.type === type
            ? {
                ...item,
                count,
                ...alertConfig,
              }
            : item
        );
      }

      return [
        ...previous,
        {
          id: `${type}-${count}-${Date.now()}`,
          type,
          count,
          ...alertConfig,
        },
      ];
    });
  }, []);

  const removeAlertByType = useCallback((type) => {
    setAlerts((previous) => previous.filter((item) => item.type !== type));
  }, []);

  const dismissAlert = useCallback((id) => {
    setAlerts((previous) => previous.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    if (suppressedTypes.note) removeAlertByType("note");
    if (suppressedTypes.inquiry) removeAlertByType("inquiry");
  }, [removeAlertByType, suppressedTypes.inquiry, suppressedTypes.note]);

  const loadCounts = useCallback(async () => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;

    try {
      const response = await siteApi.getMemberNotificationCount();
      const nextCounts = response?.success && response?.data ? response.data : {};
      if (!isMountedRef.current) return;

      setCounts(nextCounts);

      const noteUnreadCount = getNoteUnreadCount(nextCounts);
      const inquiryUnreadCount = getInquiryUnreadCount(nextCounts);
      const previousNoteCount = previousCountsRef.current.note;
      const previousInquiryCount = previousCountsRef.current.inquiry;

      if (noteUnreadCount > previousNoteCount && !suppressedTypes.note) {
        pushAlert("note", noteUnreadCount);
      } else if (noteUnreadCount < previousNoteCount || suppressedTypes.note) {
        removeAlertByType("note");
      }
      if (inquiryUnreadCount > previousInquiryCount && !suppressedTypes.inquiry) {
        pushAlert("inquiry", inquiryUnreadCount);
      } else if (inquiryUnreadCount < previousInquiryCount || suppressedTypes.inquiry) {
        removeAlertByType("inquiry");
      }
      previousCountsRef.current = {
        note: noteUnreadCount,
        inquiry: inquiryUnreadCount,
      };
    } catch {
      // 알림 폴링은 화면 진입을 막지 않아야 하므로 다음 주기에서 다시 시도한다.
    } finally {
      requestInFlightRef.current = false;
    }
  }, [pushAlert, removeAlertByType, suppressedTypes.inquiry, suppressedTypes.note]);

  useEffect(() => {
    if (!enabled) {
      isMountedRef.current = false;
      setCounts({});
      setAlerts([]);
      previousCountsRef.current = { note: 0, inquiry: 0 };
      return undefined;
    }

    isMountedRef.current = true;

    const guardedLoadCounts = async () => {
      if (!isMountedRef.current) return;
      if (document.visibilityState === "hidden") return;
      await loadCounts();
    };

    guardedLoadCounts();
    const interval = window.setInterval(guardedLoadCounts, NOTIFICATION_REFRESH_MS);
    const handleSync = () => {
      guardedLoadCounts();
    };
    const handleVisibilitySync = () => guardedLoadCounts();

    NOTIFICATION_SYNC_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleSync);
    });
    window.addEventListener("focus", handleSync);
    document.addEventListener("visibilitychange", handleVisibilitySync);

    return () => {
      isMountedRef.current = false;
      window.clearInterval(interval);
      NOTIFICATION_SYNC_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleSync);
      });
      window.removeEventListener("focus", handleSync);
      document.removeEventListener("visibilitychange", handleVisibilitySync);
    };
  }, [enabled, loadCounts]);

  return useMemo(
    () => ({
      counts,
      alerts,
      dismissAlert,
      unreadCount: getUnreadCount(counts),
      noteUnreadCount: getNoteUnreadCount(counts),
      inquiryUnreadCount: getInquiryUnreadCount(counts),
    }),
    [alerts, counts, dismissAlert]
  );
};

export default useMemberNotifications;
